import { useEffect, useState } from "react";
import useAxios from "../hooks/useAxios";
import CurrencyFormat from "react-currency-format";
import clsx from "clsx";
import { Spinner, OverlayTrigger, Popover } from "react-bootstrap";
import { AiFillExclamationCircle } from "react-icons/ai";
import { MdGroup } from "react-icons/md";
import { FiInfo, FiCopy } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import swal from "sweetalert";

const UpdatePlanAccount = ({
    account,
    accountId,
    defaultValue,
    staff,
    closedAt,
    approveAt,
    editMode,
    compactView = true,
    onRefresh
}) => {
    const [hasError, setHasError] = useState(false);
    const [oldPlanAccount, setOldPlanAccount] = useState(null);
    const [currentValue, setCurrentValue] = useState('');
    const [canUpdate, setCanUpdate] = useState({ message: '', canUpdate: false });

    const { isSuperAdmin } = useAuth();
    const [showExpense, setShowExpense] = useState(false);
    const [showPoa, setShowPoa] = useState(false);

    const [{ loading: loadingUpdatePlanAccount }, updatePlanAccount] = useAxios(
        { url: editMode ? `/temporary-planning-accounts/${accountId}` : `/plan-accounts/${accountId}`, method: 'PUT' },
        { useCache: false, manual: true }
    );

    const [{ loading: loadingGetPlanAccountExpense }, getPlanAccountExpense] = useAxios(
        { url: `/plan-accounts/${accountId}/expense`, method: 'GET' },
        { useCache: false, manual: true }
    );

    useEffect(() => {
        if (account) console.log(account);
    }, [account])

    useEffect(() => {
        if (accountId) {
            if (editMode) {
                setCanUpdate({ canUpdate: true, message: 'Por favor ingrese el monto' });
            } else {
                if (!closedAt && !approveAt) {
                    setCanUpdate({ canUpdate: true, message: 'Por favor ingrese el monto' });
                } else {
                    setCanUpdate({ canUpdate: false, message: 'No puedes actualizar la cuenta cuando el plan esta cerrado o aprobado.' });
                }
            }
        } else {
            setCanUpdate({ canUpdate: false, message: 'No puedes actualizar la cuenta.' });
        }
    }, [accountId, closedAt, approveAt, editMode]);

    useEffect(() => {
        if (defaultValue !== undefined && defaultValue !== null) setCurrentValue(defaultValue);
    }, [defaultValue]);

    const parseAmount = (val) => {
        if (val === undefined || val === null || val === '') return 0;
        if (typeof val === 'number') return isNaN(val) ? 0 : val;
        let str = String(val).replace(/\$/g, '').trim();
        if (str.includes('.') && str.includes(',')) {
            str = str.replace(/\./g, '').replace(',', '.');
        } else if (str.includes(',')) {
            str = str.replace(',', '.');
        }
        const num = parseFloat(str);
        return isNaN(num) ? 0 : num;
    };

    const handleChange = (e) => {
        setHasError(false);
        try {
            const rawVal = e.target.value;
            setCurrentValue(rawVal);
            updatePlanAccount({
                data: {
                    amount: parseAmount(rawVal),
                    applyForAllMonths: 'no'
                }
            });
        } catch (error) {
            setHasError(true);
        }
    };

    const handleFetchExpenseData = (type) => {
        getPlanAccountExpense().then((response) => {
            if (!response?.data) {
                setOldPlanAccount('vacio');
            } else {
                setOldPlanAccount(response?.data);
            }
            if (type === 'expense') setShowExpense(true);
            if (type === 'poa') setShowPoa(true);
        }).catch(() => {
            setOldPlanAccount('vacio');
        });
    };

    const handleExpense = () => handleFetchExpenseData('expense');
    const handlePoa = () => handleFetchExpenseData('poa');

    const [replicating, setReplicating] = useState(false);

    const handleReplicate = (e) => {
        e?.stopPropagation?.();
        if (!canUpdate.canUpdate || replicating) return;

        const valToReplicate = parseAmount(currentValue);

        swal({
            title: "¿Replicar a los 12 meses?",
            text: `¿Deseas aplicar el monto de $${Math.round(valToReplicate).toLocaleString('es-CL')} a todos los meses de esta cuenta?`,
            icon: "info",
            buttons: ["Cancelar", "Sí, replicar"],
        }).then((confirmed) => {
            if (confirmed) {
                setReplicating(true);
                updatePlanAccount({
                    data: {
                        amount: valToReplicate,
                        applyForAllMonths: 'si'
                    }
                }).then(() => {
                    setReplicating(false);
                    swal({
                        title: "¡Replicado!",
                        text: "El monto se ha replicado a los 12 meses correctamente.",
                        icon: "success",
                        timer: 1800,
                        buttons: false
                    });
                    onRefresh?.();
                }).catch(() => {
                    setReplicating(false);
                    setHasError(true);
                    swal({
                        title: "Error",
                        text: "No se pudo replicar el monto.",
                        icon: "error"
                    });
                });
            }
        });
    };

    // Popover content for compact view inspection
    const popoverContent = (
        <Popover id={`popover-acc-${accountId}`} style={{ maxWidth: 260, fontSize: '0.75rem', zIndex: 1060 }}>
            <Popover.Header as="h6" className="py-1 px-2 fw-bold" style={{ fontSize: '0.78rem' }}>
                Referencias del Mes
            </Popover.Header>
            <Popover.Body className="p-2">
                {loadingGetPlanAccountExpense ? (
                    <div className="text-center py-2">
                        <Spinner variant="primary" size="sm" animation="border" />
                    </div>
                ) : (
                    <div>
                        <div className="mb-1 pb-1 border-bottom">
                            <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Gasto Año Anterior:</span>
                            {oldPlanAccount && oldPlanAccount !== 'vacio' ? (
                                <strong className="text-primary">${Number(oldPlanAccount?.expense || 0)?.toLocaleString()}</strong>
                            ) : oldPlanAccount === 'vacio' ? (
                                <span className="text-muted fst-italic">Sin registros</span>
                            ) : (
                                <button
                                    onClick={handleExpense}
                                    className="btn btn-link btn-xs p-0 text-primary"
                                    style={{ fontSize: '0.72rem', textDecoration: 'none' }}
                                >
                                    Cargar gasto anterior
                                </button>
                            )}
                        </div>

                        {isSuperAdmin && (
                            <div>
                                <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>POA:</span>
                                {oldPlanAccount && oldPlanAccount !== 'vacio' ? (
                                    <strong className="text-secondary">
                                        {oldPlanAccount?.poa_value ? `$${Number(oldPlanAccount?.poa_value)?.toLocaleString()}` : 'No ingresado'}
                                    </strong>
                                ) : oldPlanAccount === 'vacio' ? (
                                    <span className="text-muted fst-italic">Sin registros</span>
                                ) : (
                                    <button
                                        onClick={handlePoa}
                                        className="btn btn-link btn-xs p-0 text-secondary"
                                        style={{ fontSize: '0.72rem', textDecoration: 'none' }}
                                    >
                                        Cargar POA
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Popover.Body>
        </Popover>
    );

    return (
        <div className="pm-cell-container">
            <div className="d-flex align-items-center position-relative">
                {Boolean(staff) && (
                    <MdGroup
                        className="text-primary me-1 flex-shrink-0"
                        style={{ fontSize: 13, cursor: 'help' }}
                        title="Afectada por la dotación del mes"
                    />
                )}

                <CurrencyFormat
                    className={clsx(["form-control pm-numeric-input"], {
                        'val-error': hasError,
                        'val-positive': !hasError && Number(currentValue) > 0,
                        'val-negative': !hasError && Number(currentValue) < 0,
                    })}
                    value={currentValue}
                    decimalSeparator=","
                    thousandSeparator="."
                    isNumericString
                    readOnly={!canUpdate.canUpdate}
                    title={canUpdate.message}
                    prefix="$ "
                    onValueChange={(e) => handleChange({ target: { name: 'amount', value: e.value } })}
                    placeholder="$ 0"
                />

                {loadingUpdatePlanAccount && (
                    <div className="position-absolute end-0 me-1" style={{ top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Spinner variant="primary" style={{ width: 14, height: 14 }} animation="border" />
                    </div>
                )}

                {!loadingUpdatePlanAccount && hasError && (
                    <div className="position-absolute end-0 me-1" style={{ top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <AiFillExclamationCircle className="text-danger" style={{ fontSize: 13 }} />
                    </div>
                )}

                {/* Replicate button */}
                {accountId && canUpdate.canUpdate && (
                    <button
                        type="button"
                        className="pm-replicate-btn ms-1 flex-shrink-0"
                        title="Replicar este monto a los 12 meses"
                        onClick={handleReplicate}
                        disabled={loadingUpdatePlanAccount || replicating}
                    >
                        {replicating ? (
                            <Spinner variant="primary" style={{ width: 11, height: 11 }} animation="border" />
                        ) : (
                            <FiCopy style={{ fontSize: 11 }} />
                        )}
                    </button>
                )}

                {/* Popover trigger button in compact mode */}
                {accountId && compactView && (
                    <OverlayTrigger trigger="click" rootClose placement="top" overlay={popoverContent}>
                        <button
                            type="button"
                            className="pm-ref-popover-btn ms-1 flex-shrink-0"
                            title="Ver referencias (Año anterior / POA)"
                        >
                            <FiInfo />
                        </button>
                    </OverlayTrigger>
                )}
            </div>

            {/* Detailed view mode (when compactView is false) */}
            {accountId && !compactView && (
                <div className="pm-ref-box">
                    {loadingGetPlanAccountExpense ? (
                        <div className="text-center py-1">
                            <Spinner variant="primary" style={{ width: 12, height: 12 }} animation="border" />
                        </div>
                    ) : (
                        <>
                            {showExpense && oldPlanAccount && oldPlanAccount !== 'vacio' ? (
                                <span className="pm-ref-pill pm-ref-pill-ant" title="Gasto año anterior">
                                    Ant: {Number(oldPlanAccount?.expense || 0)?.toLocaleString()}$
                                </span>
                            ) : (
                                <span
                                    onClick={handleExpense}
                                    className="pm-ref-pill pm-ref-pill-ant"
                                    title="Click para ver gasto año anterior"
                                >
                                    {oldPlanAccount === 'vacio' ? 'Ant: N/A' : 'Ant: Ver'}
                                </span>
                            )}

                            {isSuperAdmin && (
                                showPoa && oldPlanAccount && oldPlanAccount !== 'vacio' ? (
                                    <span className="pm-ref-pill pm-ref-pill-poa" title="POA">
                                        POA: {oldPlanAccount?.poa_value ? `${Number(oldPlanAccount?.poa_value)?.toLocaleString()}$` : 'N/A'}
                                    </span>
                                ) : (
                                    <span
                                        onClick={handlePoa}
                                        className="pm-ref-pill pm-ref-pill-poa"
                                        title="Click para ver POA"
                                    >
                                        {oldPlanAccount === 'vacio' ? 'POA: N/A' : 'POA: Ver'}
                                    </span>
                                )
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default UpdatePlanAccount;